import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext } from 'react';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { BreakdownBy } from '../components/breakdown-by';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components/text';
import { rateOfChange } from '@/pages/reports/custom-reports/transformers/card/cardUtils';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';

import {
  GROUP_BY_OPTIONS,
  BREAKDOWN_OPTIONS,
  metricDefinitions,
  basedOnValues,
} from './performance-table-constants';

// Interfaces and Type Definitions
interface Option {
  label: string;
  id: number | string;
}

// Format job role display names
const formatJobRole = (role: string) => {
  const jobRoleMap: Record<string, string> = {
    primary_hiring_manager: 'Hiring manager',
    primary_recruiter: 'Recruiter',
    coordinator: 'Coordinator',
    sourcer: 'External recruiter',
    hiring_manager: 'Hiring manager',
    recruiter: 'Recruiter',
    unknown: 'Users with manage job access',
  };
  return jobRoleMap[role] || role;
};

interface PerformanceTableProps {
  isReadOnly?: boolean;
  groupBy1?: any;
  groupBy2?: Option;
  customData?: any[];
  isPrintView?: boolean;
  customBreakdownChange?: (_key: string, _option: Option) => void;
  renderTitle?: () => React.ReactNode;
  onDrillDownOpen?: (_data: any) => void;
  isStaticReport?: boolean;
  onSortChange?: (_sortState: string) => void;
  isLoading?: boolean;
  showByRecruiter?: boolean;
  isEditMode?: boolean;
  tableColStyles?: any;
  onTableColDataChange?: (_tableColData: {
    order: string[];
    visibility: Record<string, boolean>;
  }) => void;
  initialTableColData?: {
    order: string[];
    visibility: Record<string, boolean>;
  };
  skipLocalStorage?: boolean;
  feReportsV3?: boolean;
  isFullScreen?: boolean;
}

export const PerformanceTable: React.FC<PerformanceTableProps> = ({
  isReadOnly = false,
  groupBy1 = null,
  groupBy2 = null,
  isPrintView = false,
  customData = [],
  customBreakdownChange = () => {},
  renderTitle = () => <></>,
  onDrillDownOpen = () => {},
  isStaticReport = false,
  onSortChange = () => {},
  isLoading = false,
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage: _skipLocalStorage = false,
  feReportsV3: _feReportsV3 = false,
  isFullScreen = false,
}) => {
  const { feDrilldown } = useKFlags();
  const { consumer } = useContext(ReportSelectionContext);
  const groupBy_1 =
    !groupBy1 || groupBy1 === 'action_performed_by_user'
      ? GROUP_BY_OPTIONS[0]
      : groupBy1;

  const dataToUse = customData;
  const columns = useMemo(() => {
    const header = groupBy_1 || GROUP_BY_OPTIONS[0];

    const headerColumn = renderHeader({
      accessor: 'header',
      title: header?.label || 'Name',
      width: 280,
      expand: false,
      sort: true,

      renderFooter: () => <KText size="sm-semi-bold">Total</KText>,
      isReadOnly,
      columnConfig: false,
    });

    const customHeaderColumn = {
      ...headerColumn,
      Cell: (props: any) => {
        const formattedValue = formatJobRole(props.cell.value);

        const modifiedProps = {
          ...props,
          cell: {
            ...props.cell,
            value: formattedValue,
          },
        };

        return headerColumn.Cell(modifiedProps);
      },
      sortType: (rowA: any, rowB: any) => {
        const valueA = formatJobRole(rowA?.values?.header) || '';
        const valueB = formatJobRole(rowB?.values?.header) || '';

        return String(valueA)?.localeCompare(String(valueB));
      },
    };

    return [
      customHeaderColumn,

      createDrillDownColumn(
        'Sourced applications',
        'sourced_candidates',
        (row) => `${row?.sourced_candidates}`,
        'Sourced applications',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.sourced_candidates_total}`;
        }
      ),
      createDrillDownColumn(
        'Screened applications',
        'screened_candidates',
        (row) => `${row?.screened_candidates}`,
        'Screened applications',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.screened_candidates_total}`;
        }
      ),
      createDrillDownColumn(
        'Interviews scheduled',
        'interviews_scheduled',
        (row) => `${row?.interviews_scheduled}`,
        'Interviews scheduled',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.interviews_scheduled_total}`;
        }
      ),
      createDrillDownColumn(
        'Interview reschedules',
        'interviews_rescheduled',
        (row) => `${row?.interviews_rescheduled}`,
        'Interview reschedules',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.interview_rescheduled_total}`;
        }
      ),
      createDrillDownColumn(
        'Interviews conducted',
        'interviews_conducted',
        (row) => `${row?.interviews_conducted}`,
        'Interviews conducted',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.interviews_conducted_total}`;
        }
      ),
      createDrillDownColumn(
        'Interviews canceled',
        'interviews_cancelled',
        (row) => `${row?.interviews_cancelled}`,
        'Interviews canceled',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.interviews_cancelled_total}`;
        }
      ),
      createDrillDownColumn(
        'Offers sent',
        'offers_sent',
        (row) => `${row?.offers_sent}`,
        'Offers sent',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.offers_sent_total}`;
        }
      ),
      createDrillDownColumn(
        'Offer revisions done',
        'offer_revisions_done',
        (row) => `${row?.offer_revisions_done}`,
        'Offer revisions done',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.offer_revisions_done_total}`;
        }
      ),
      {
        ...createDrillDownColumn(
          'Avg. time to offer',
          'time_to_offer',
          (row) =>
            `${secondsToHumanTime(row?.time_to_offer / row?.offers_sent)}`,
          'Avg. time to offer'
        ),
        sortType: (rowA, rowB) => {
          const offersA = parseInt(rowA.original?.offers_sent) || 0;
          const timeToOfferA = parseInt(rowA.original?.time_to_offer) || 0;
          const offersB = parseInt(rowB.original?.offers_sent) || 0;
          const timeToOfferB = parseInt(rowB.original?.time_to_offer) || 0;

          const avgA = offersA > 0 ? timeToOfferA / offersA : 0;
          const avgB = offersB > 0 ? timeToOfferB / offersB : 0;

          return avgA - avgB;
        },
      },

      {
        ...createDrillDownColumn(
          'Offer acceptance rate',
          'offers_accepted',
          (row) =>
            `${rateOfChange({
              numerator: row?.offers_accepted || 0,
              denominator: row?.offers_sent || 0,
            })}%`,
          'Offer acceptance rate'
        ),
        sortType: (rowA, rowB) => {
          const offersAcceptedA = parseInt(rowA.original?.offers_accepted) || 0;
          const offersSentA = parseInt(rowA.original?.offers_sent) || 0;
          const offersAcceptedB = parseInt(rowB.original?.offers_accepted) || 0;
          const offersSentB = parseInt(rowB.original?.offers_sent) || 0;

          const rateA = offersSentA > 0 ? offersAcceptedA / offersSentA : 0;
          const rateB = offersSentB > 0 ? offersAcceptedB / offersSentB : 0;

          return rateA - rateB;
        },
      },
      createDrillDownColumn(
        'Hired',
        'hired_candidates',
        (row) => `${row?.hired_candidates}`,
        'Hired',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.hired_candidates_total}`;
        }
      ),
      {
        ...createDrillDownColumn(
          'Avg. time to hire',
          'time_to_hire',
          (row) =>
            `${secondsToHumanTime(row?.time_to_hire / row?.hired_candidates)}`,
          'Avg. time to hire'
        ),
        sortType: (rowA, rowB) => {
          const hiredA = parseInt(rowA.original?.hired_candidates) || 0;
          const timeToHireA = parseInt(rowA.original?.time_to_hire) || 0;
          const hiredB = parseInt(rowB.original?.hired_candidates) || 0;
          const timeToHireB = parseInt(rowB.original?.time_to_hire) || 0;

          const avgA = hiredA > 0 ? timeToHireA / hiredA : 0;
          const avgB = hiredB > 0 ? timeToHireB / hiredB : 0;

          return avgA - avgB;
        },
      },
      createDrillDownColumn(
        'Rejected',
        'rejected_candidates',
        (row) => `${row?.rejected_candidates}`,
        'Rejected',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.rejected_candidates_total}`;
        }
      ),
    ];
  }, [groupBy1]); // eslint-disable-line react-hooks/exhaustive-deps

  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: GROUP_BY_OPTIONS,
      selected: groupBy_1 || GROUP_BY_OPTIONS[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: BREAKDOWN_OPTIONS,
      selected: groupBy2 || BREAKDOWN_OPTIONS[0],
    },
  ];

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  const { handleDrillDown } = useDrillDownHandler(feDrilldown, onDrillDownOpen);

  function createDrillDownColumn(
    title: string,
    accessor: string,
    renderContent: (_row: any) => string,
    drillDownHeader: string,
    footerRenderer?: (_info: any) => string
  ) {
    return renderCell({
      title,
      accessor,
      width: isReadOnly ? 160 : 210,
      sort: true,
      metricDefinition: metricDefinitions[title],
      basedOn: basedOnValues[title],
      category: 'Recruiter performance',
      enabletooltip: true,
      renderFooter: footerRenderer
        ? (info) =>
            createDrillDownContent(
              info?.rows?.[0]?.original,
              title,
              drillDownHeader,
              () => footerRenderer(info),
              true
            )
        : undefined,
      render: (_, row) => {
        return createDrillDownContent(
          row,
          title,
          drillDownHeader,
          renderContent
        );
      },
    });
  }

  // All metric fields to check (excluding _total fields and metadata fields)
  const metricFields = [
    'sourced_candidates',
    'screened_candidates',
    'interviews_scheduled',
    'interviews_conducted',
    'interviews_rescheduled',
    'interviews_cancelled',
    'offers_sent',
    'offers_accepted',
    'time_to_offer',
    'offer_revisions_done',
    'hired_candidates',
    'time_to_hire',
    'rejected_candidates',
  ];

  const getFieldWithData = (): string => {
    if (!dataToUse?.length) {
      return 'sourced_candidates';
    }

    for (const field of metricFields) {
      if (dataToUse?.some((row) => row[field] > 0)) {
        return field;
      }
    }

    return 'sourced_candidates';
  };

  const fieldToUse = getFieldWithData();

  const getBreakdownMarginRight = () => {
    if (isPrintView) return '0';
    if (isReportTableEmpty(dataToUse, fieldToUse)) return '35px';
    if (isStaticReport) return '75px';
    if (isEditMode) return '55px';
    return '35px';
  };

  function createDrillDownContent(
    row: any,
    title: string,
    drillDownHeader: string,
    contentRenderer?: (_row: any) => string,
    isFooter?: boolean
  ) {
    return (
      <Flex
        _hover={{ textDecoration: feDrilldown ? 'underline' : 'none' }}
        cursor={feDrilldown ? 'pointer' : 'default'}
        onClick={() =>
          handleDrillDown(
            row,
            drillDownHeader,
            isFooter,
            { group_by_1: groupBy1, group_by_2: groupBy2 },
            null,
            contentRenderer(row)?.replace(/\(\d+(\.\d+)?%\)/g, '')
          )
        }
      >
        <KText size="sm-semi-bold">
          {contentRenderer ? contentRenderer(row) : ''}
        </KText>
      </Flex>
    );
  }

  return (
    <Wrapper
      role="group"
      isReadOnly={isReadOnly || isStaticReport}
      h="calc(100% - 25px)"
      position="relative"
    >
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label={'Recruiter performance'}
                metricDefinition="Comprehensive breakdown of recruiter performance metrics"
              />
            </Box>
          )}
          <Flex
            gap="8px"
            justifyContent="end"
            mb="8px"
            mr={getBreakdownMarginRight()}
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) => {
                  //remove this for feReportsV3
                  if (key === 'group_by_2' && customBreakdownChange) {
                    // Only set group_by_1 to default if it's not already set
                    if (!groupBy1 || groupBy1 === 'action_performed_by_user') {
                      customBreakdownChange('group_by_1', GROUP_BY_OPTIONS[0]);
                    }
                    customBreakdownChange(key, option);
                  } else {
                    customBreakdownChange?.(key, option);
                  }
                }}
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper
          data={dataToUse}
          field={fieldToUse}
          styles={{ margin: '100px auto 90px auto !important' }}
        >
          <Table
            tableId="performance-table"
            skipLocalStorage={false}
            isColumnLevelSorting
            enableColumnConfig={isStaticReport ? true : isEditMode}
            tableColStyles={{
              ...tableColStyles,
              trigger: {
                ...tableColStyles.trigger,
                position: 'absolute',
                right: isStaticReport ? '35px' : '15px',
                top: '0px',
              },
            }}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            defaultSortBy={[{ id: 'header', desc: false }]}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            tableContainerStyles={{ position: 'unset' }}
            getTableState={handleSortChange}
            onTableColDataChange={onTableColDataChange}
            initialTableColData={initialTableColData}
          />
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
